import { useState } from 'react';
import { CopySimple, PencilSimple } from '@phosphor-icons/react';
import type { Scene } from '@spine-workspace/shared';

interface SceneCardProps {
  scene: Scene;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
  onRename: (id: string, name: string) => void;
  onDuplicate: (id: string) => void;
}

export default function SceneCard({ scene, onOpen, onDelete, onRename, onDuplicate }: SceneCardProps) {
  const [renamingScene, setRenamingScene] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  const updatedDate = new Date(scene.updatedAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  function startRename(e: React.MouseEvent) {
    e.stopPropagation();
    setNameDraft(scene.name);
    setRenamingScene(true);
  }

  function commitRename() {
    const trimmed = nameDraft.trim();
    if (trimmed && trimmed !== scene.name) onRename(scene.id, trimmed);
    setRenamingScene(false);
  }

  return (
    <div
      data-testid={`scene-card-${scene.id}`}
      className="bg-bg-surface border border-border-subtle rounded-xl overflow-hidden group cursor-pointer hover:border-accent/30 hover:shadow-md transition-all duration-normal"
      onClick={() => !renamingScene && onOpen(scene.id)}
    >
      {/* Thumbnail */}
      <div className="aspect-video bg-bg-canvas canvas-dot-grid flex items-center justify-center relative">
        {scene.thumbnailUrl ? (
          <img
            src={scene.thumbnailUrl}
            alt={scene.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex flex-col items-center gap-2 text-text-tertiary">
            <svg className="w-10 h-10 opacity-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="font-sans text-xs">No preview</span>
          </div>
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-normal flex items-center justify-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onOpen(scene.id); }}
            className="bg-accent text-white font-sans font-medium text-xs px-3 py-1.5 rounded-md hover:bg-accent-hover hover:shadow-glow transition-all duration-normal"
          >
            Open
          </button>
          <button
            data-testid={`scene-duplicate-${scene.id}`}
            onClick={(e) => { e.stopPropagation(); onDuplicate(scene.id); }}
            title="Duplicate scene"
            className="bg-bg-elevated border border-border text-text-secondary font-sans text-xs px-2 py-1.5 rounded-md hover:bg-bg-hover transition-all duration-normal"
          >
            <CopySimple size={13} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (confirm(`Delete "${scene.name}"?`)) onDelete(scene.id);
            }}
            className="bg-error-muted text-error border border-error/30 font-sans text-xs px-3 py-1.5 rounded-md hover:bg-error/20 transition-all duration-normal"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="p-3 border-t border-border-subtle">
        {renamingScene ? (
          <input
            autoFocus
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
              if (e.key === 'Escape') setRenamingScene(false);
              e.stopPropagation();
            }}
            onBlur={commitRename}
            onClick={(e) => e.stopPropagation()}
            data-testid={`scene-name-input-${scene.id}`}
            className="w-full bg-bg-input border border-accent rounded px-1.5 py-0.5 text-sm font-sans text-text-primary outline-none"
          />
        ) : (
          <div className="flex items-center gap-1 group/name">
            <p
              data-testid={`scene-name-${scene.id}`}
              className="font-sans text-sm font-medium text-text-primary truncate flex-1"
              onDoubleClick={startRename}
            >
              {scene.name}
            </p>
            <button
              onClick={startRename}
              data-testid={`scene-rename-btn-${scene.id}`}
              className="opacity-0 group-hover/name:opacity-100 w-5 h-5 flex items-center justify-center rounded text-text-tertiary hover:text-accent hover:bg-accent-muted transition-all duration-fast shrink-0"
              title="Rename scene"
            >
              <PencilSimple size={11} />
            </button>
          </div>
        )}
        <div className="flex items-center justify-between mt-1">
          <span className="font-mono text-xs text-text-tertiary">
            {scene.canvasWidth}×{scene.canvasHeight}
          </span>
          <span className="font-sans text-xs text-text-tertiary">{updatedDate}</span>
        </div>
      </div>
    </div>
  );
}
